import type { KpisOverviewResponse, Granularity } from "../kpi-api-types.ts";
import { type DateScopeOverrideInput } from "./date-scope.ts";
import { validateMetricKeysList } from "./metrics.ts";
import {
  type AnalyticsScope,
  createAnalyticsScopeFromTracifyContext,
} from "./scope.ts";
import {
  getTracifyApiJson,
  type TracifyApiBasePath,
} from "./tracify-api-client.ts";
import type { TracifyContext } from "../ai/request-context.ts";

export type KpisOverviewFetchInput = {
  granularity?: Granularity | null;
  requested_metrics?: string[] | null;
};

export type FetchKpisOverviewOptions = {
  baseUrl: string;
  scope: AnalyticsScope;
  input: KpisOverviewFetchInput;
  token: string;
  timeoutMs?: number;
  maxRetries?: number;
  retryDelayMs?: number;
  requestId?: string;
  apiBasePath?: TracifyApiBasePath;
};

export type FetchKpisOverviewWithContextOptions = Omit<
  FetchKpisOverviewOptions,
  "scope" | "input"
> & {
  context: TracifyContext;
  input: KpisOverviewFetchInput & DateScopeOverrideInput;
};

const DEFAULT_GRANULARITY: Granularity = "none";

function normalizeMetrics(requestedMetrics: string[] | null | undefined) {
  const validated = validateMetricKeysList(
    requestedMetrics,
    "requested_metrics"
  );
  return validated ? [...validated].sort() : null;
}

function appendGranularity(
  searchParams: URLSearchParams,
  granularity: Granularity
) {
  searchParams.append("granularity", granularity);
}

function appendFilters(searchParams: URLSearchParams, scope: AnalyticsScope) {
  if (scope.filters) {
    searchParams.append("filters", scope.filters);
  }
}

function appendCustomerTarget(
  searchParams: URLSearchParams,
  scope: AnalyticsScope
) {
  if (scope.customerType && scope.customerType !== "total") {
    searchParams.append("filter_and_sort_target", scope.customerType);
  }
}

function appendComparison(
  searchParams: URLSearchParams,
  comparison: { start: string; end: string } | null
) {
  if (!comparison) {
    return;
  }

  searchParams.append("compare_previous_date_range", "true");
  searchParams.append("compare_start_date", comparison.start);
  searchParams.append("compare_end_date", comparison.end);
}

export function buildKpisOverviewSearchParams(
  scope: AnalyticsScope,
  input: KpisOverviewFetchInput
): URLSearchParams {
  const granularity = input.granularity ?? DEFAULT_GRANULARITY;
  const requestedMetrics = normalizeMetrics(input.requested_metrics);
  const searchParams = new URLSearchParams();

  searchParams.append("preset_id", scope.presetId);
  searchParams.append("start_date", scope.startDate);
  searchParams.append("end_date", scope.endDate);

  for (const csid of scope.storeIds) {
    searchParams.append("csids", csid);
  }

  if (requestedMetrics) {
    for (const metric of requestedMetrics) {
      searchParams.append("requested_metrics", metric);
      searchParams.append("granularity_only_metrics", metric);
    }
  }

  appendGranularity(searchParams, granularity);
  appendFilters(searchParams, scope);
  appendCustomerTarget(searchParams, scope);
  appendComparison(searchParams, scope.comparison);

  return searchParams;
}

export async function fetchKpisOverviewWithScope({
  baseUrl,
  scope,
  input,
  token,
  timeoutMs,
  maxRetries,
  retryDelayMs,
  requestId,
  apiBasePath,
}: FetchKpisOverviewOptions): Promise<KpisOverviewResponse> {
  return getTracifyApiJson<KpisOverviewResponse>({
    baseUrl,
    path: "/analytics/kpis/overview/",
    searchParams: buildKpisOverviewSearchParams(scope, input),
    token,
    resource: "KPI overview",
    timeoutMs,
    maxRetries,
    retryDelayMs,
    requestId,
    apiBasePath,
  });
}

export async function fetchKpisOverviewWithContext({
  context,
  input,
  ...options
}: FetchKpisOverviewWithContextOptions): Promise<KpisOverviewResponse> {
  return fetchKpisOverviewWithScope({
    ...options,
    scope: createAnalyticsScopeFromTracifyContext(context, input),
    input,
  });
}
